import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Link2, Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { classificationsApi } from '@/features/classifications/api'
import { servicenowCmdbApi, type CMDBSyncItem } from '../api'

interface MatchCIDialogProps {
    item: CMDBSyncItem | null
    onOpenChange: (open: boolean) => void
}

/** The ObjectID the old Add derived from a CI name, kept so a created object is named the same way. */
const objectIdFromName = (name: string | null) => (name || '').replace(/[^a-zA-Z0-9_.-]/g, '').substring(0, 250)

/**
 * Match a ServiceNow CI to the Vault Sentinel object it is — an existing one, or one created for
 * it. Matching changes nothing in either system; Align, Apply and Remove act on the match.
 */
export function MatchCIDialog({ item, onOpenChange }: MatchCIDialogProps) {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [mode, setMode] = useState<'existing' | 'new'>('existing')
    const [search, setSearch] = useState('')
    const [selected, setSelected] = useState<string | null>(null)
    const [newId, setNewId] = useState('')
    const [newDescription, setNewDescription] = useState('')
    const [newService, setNewService] = useState<'Object' | 'Service'>('Object')
    const [classificationId, setClassificationId] = useState<string>('')
    const [error, setError] = useState<string | null>(null)

    // Reset for each CI. The suggestion is preselected, never applied: confirming is the operator's.
    useEffect(() => {
        if (!item) return
        setMode('existing')
        setSearch(item.suggestedObjectId ?? item.ciName ?? '')
        setSelected(item.suggestedObjectId ?? null)
        setNewId(objectIdFromName(item.ciName))
        setNewDescription(item.ciDescription || item.ciName || '')
        setNewService(/service/i.test(item.ciClass || '') ? 'Service' : 'Object')
        setClassificationId('')
        setError(null)
    }, [item])

    const { data: candidates = [], isFetching } = useQuery({
        queryKey: ['cmdb-object-search', search],
        queryFn: async () => (await servicenowCmdbApi.searchObjects(search)).data?.data ?? [],
        enabled: !!item && mode === 'existing',
    })

    // Returns the ARRAY: ['classifications'] is a shared cache key, and every other caller stores
    // the array under it. Storing anything else there breaks the canvas.
    const { data: classifications = [] } = useQuery({
        queryKey: ['classifications'],
        queryFn: async () => {
            const r = await classificationsApi.list()
            return ((r.data as any).data || []) as any[]
        },
        enabled: !!item && mode === 'new',
    })
    const activeClasses = useMemo(
        () => (classifications as any[]).filter((c) => Number(c.Active ?? 1) === 1).sort((a, b) => String(a.Name).localeCompare(String(b.Name))),
        [classifications],
    )
    // Preselect a classification named like the CI's class, when there is exactly that.
    useEffect(() => {
        if (mode !== 'new' || classificationId || !item?.ciClass) return
        const same = activeClasses.find((c) => String(c.Name).toLowerCase() === item.ciClass!.toLowerCase())
        if (same) setClassificationId(String(same.id))
    }, [mode, activeClasses, item, classificationId])

    const matchMutation = useMutation({
        mutationFn: async () => {
            if (!item) throw new Error('No CI')
            if (mode === 'existing') {
                return (await servicenowCmdbApi.matchItem(item.id, selected!)).data?.data
            }
            return (await servicenowCmdbApi.matchNewObject(item.id, {
                objectId: newId.trim(),
                description: newDescription.trim(),
                objectService: newService,
                ClassificationID: Number(classificationId),
            })).data?.data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
            toast({
                title: 'Matched',
                description: `${item?.ciName} is ${data?.objectId}${data && 'created' in data && data.created ? ' (new object)' : ''}. Nothing is written to ServiceNow until you align it.`,
            })
            onOpenChange(false)
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    })

    const canConfirm = mode === 'existing'
        ? !!selected
        : newId.trim().length > 0 && !!classificationId

    return (
        <Dialog open={!!item} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Match {item?.ciName}</DialogTitle>
                    <DialogDescription>
                        Choose the Vault Sentinel object this ServiceNow CI is. Matching changes nothing in either system — you align them afterwards.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex gap-2">
                    <Button type="button" size="sm" variant={mode === 'existing' ? 'default' : 'outline'} onClick={() => { setMode('existing'); setError(null) }}>
                        <Link2 className="h-3.5 w-3.5 mr-1.5" /> Existing object
                    </Button>
                    <Button type="button" size="sm" variant={mode === 'new' ? 'default' : 'outline'} onClick={() => { setMode('new'); setError(null) }}>
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> New object
                    </Button>
                </div>

                {mode === 'existing' ? (
                    <div className="space-y-2">
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by Object ID or description" />
                        <div className="max-h-72 overflow-auto rounded-md border divide-y">
                            {candidates.length === 0 ? (
                                <p className="p-3 text-sm text-muted-foreground">{isFetching ? 'Searching…' : 'No objects match. Create a new one instead.'}</p>
                            ) : candidates.map((c) => {
                                const taken = !!c.matchedToCi
                                const isSelected = selected === c.objectId
                                return (
                                    <button
                                        key={c.objectId}
                                        type="button"
                                        disabled={taken}
                                        onClick={() => setSelected(c.objectId)}
                                        className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm ${isSelected ? 'bg-primary/10' : 'hover:bg-muted/60'} disabled:cursor-not-allowed disabled:opacity-50`}
                                    >
                                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isSelected ? 'text-primary' : 'text-transparent'}`} />
                                        <span className="min-w-0 flex-1">
                                            <span className="font-medium">{c.objectId}</span>
                                            {c.objectId === item?.suggestedObjectId && <span className="ml-2 text-xs text-primary">same name</span>}
                                            {c.description && <span className="block truncate text-xs text-muted-foreground">{c.description}</span>}
                                            {taken && <span className="block text-xs text-amber-600">Already matched to {c.matchedToCi}</span>}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{c.objectService}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="grid gap-3">
                        <div className="grid gap-1.5">
                            <Label htmlFor="match-new-id">Object ID</Label>
                            <Input id="match-new-id" value={newId} onChange={(e) => setNewId(e.target.value)} />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="match-new-desc">Description</Label>
                            <Input id="match-new-desc" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="grid gap-1.5">
                                <Label>Type</Label>
                                <Select value={newService} onValueChange={(v) => setNewService(v as 'Object' | 'Service')}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Object">Object</SelectItem>
                                        <SelectItem value="Service">Service</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Classification</Label>
                                <Select value={classificationId} onValueChange={setClassificationId}>
                                    <SelectTrigger><SelectValue placeholder="Required — sets the recovery band" /></SelectTrigger>
                                    <SelectContent>
                                        {activeClasses.map((c) => (
                                            <SelectItem key={c.id} value={String(c.id)}>{c.Name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {error && <p className="text-sm text-destructive">{error}</p>}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={() => { setError(null); matchMutation.mutate() }} disabled={!canConfirm || matchMutation.isPending}>
                        {matchMutation.isPending ? 'Matching…' : mode === 'new' ? 'Create and match' : 'Match'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
